import { useMemo } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Page, { type PageProps } from "@/v2-backoffice-client/components/page"
import TranslationBoard from "@/v2-backoffice-client/translation/TranslationBoard"
import TranslationPublishButton from "@/v2-backoffice-client/translation/publish/TranslationPublishButton"
import TranslationAddRowButton from "@/v2-backoffice-client/translation/addRow/TranslationAddRowButton"
import { useTranslationCatalogQuery } from "@/v2-backoffice-client/translation/catalog/translation.catalog.query"
import { buildTranslationBoard } from "@/v2-backoffice-client/translation/translation.board.utils"

export type TranslationPageProps = PageProps

function TranslationPage(props: TranslationPageProps) {
  const { className, ...pageProps } = props

  // Owned here (not in TranslationBoard) because the header's Add Text
  // button needs `board`/`onSaved` too — lifted just far enough that both
  // siblings can reach it.
  const { data, loading, error, refetch } = useTranslationCatalogQuery()

  // The board pass is the tab's hot path: a few thousand rows aggregated
  // into rows, category groups, and fallback diffs. Keyed on the query
  // result so it re-runs only per (re)fetch, never per render.
  const board = useMemo(() => buildTranslationBoard(data), [data])

  // Returns the refetch promise so callers that navigate afterwards (the
  // Add-type-values flow) can wait for the board to actually carry the new
  // rows before landing on them.
  function onSaved() {
    return refetch()
  }

  return (
    <Page className={cn("translation-page", className)} {...pageProps}>
      <Page.Header>
        <Page.Title>Features</Page.Title>
        {/* Publish is the page's one global action: it ships the whole
            database state regardless of which view was edited. Everything
            else navigates via the sidebar. */}
        <Page.Header.Actions>
          <TranslationAddRowButton
            board={board}
            onSaved={onSaved}
            disabled={!data}
          />
          <TranslationPublishButton />
        </Page.Header.Actions>
      </Page.Header>

      <Page.Content className="flex-1 min-h-0 overflow-hidden py-2">
        <TranslationBoard
          board={board}
          loading={loading}
          error={error}
          onSaved={onSaved}
        />
      </Page.Content>
    </Page>
  )
}

export default TranslationPage
